import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import ArrowDropUp from "@material-ui/icons/ArrowDropUp";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import Store from "@material-ui/icons/Store";
import DateRange from "@material-ui/icons/DateRange";
import LocalOffer from "@material-ui/icons/LocalOffer";
import Update from "@material-ui/icons/Update";
import AssignmentReturn from "@material-ui/icons/AssignmentReturn";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Danger from "components/Typography/Danger.js";

import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

const GRAPHQL_URL =
  (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

function gql(query, variables) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    credentials: "include",
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());
}

function formatNum(val) {
  if (val >= 1000000)
    return (val / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (val >= 1000) return (val / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return Number(val || 0).toLocaleString("vi-VN");
}

const PERIODS = [
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" },
];

const PRODUCT_METRICS = [
  { value: "revenue", label: "By net revenue" },
  { value: "quantity", label: "By quantity" },
];

const REV_PERIODS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 days" },
  { value: "month", label: "This month" },
  { value: "lastmonth", label: "Last month" },
];

const REV_TABS = [
  { value: "date", label: "By date" },
  { value: "hour", label: "By hour" },
  { value: "day", label: "By day" },
];

const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function DropdownButton({ label, options, value, onChange }) {
  const [anchor, setAnchor] = React.useState(null);
  const current = options.find((o) => o.value === value);
  return (
    <>
      <button
        onClick={(e) => setAnchor(e.currentTarget)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 10px",
          border: "1px solid #d0d0d0",
          borderRadius: "6px",
          backgroundColor: "#fff",
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 500,
          color: "#333",
        }}
      >
        {current?.label}
        <KeyboardArrowDown style={{ fontSize: "16px", color: "#666" }} />
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {options.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              onChange(o.value);
              setAnchor(null);
            }}
            style={{ fontSize: "13px" }}
          >
            {o.value === value && (
              <span
                style={{
                  color: "#1976d2",
                  marginRight: "8px",
                  fontWeight: 700,
                }}
              >
                ✓
              </span>
            )}
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function HorizontalBarChart({ data, valueKey, labelKey, loading }) {
  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}
      >
        <CircularProgress size={28} />
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          color: "#aaa",
          padding: "40px 0",
          fontSize: "14px",
        }}
      >
        No data for this period
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => d[valueKey] || 0));

  return (
    <div style={{ padding: "4px 0" }}>
      {data.map((item, i) => {
        const val = item[valueKey] || 0;
        const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
              gap: "8px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#555",
                width: "160px",
                flexShrink: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "right",
              }}
              title={item[labelKey]}
            >
              {item[labelKey]}
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: "#f0f0f0",
                borderRadius: "3px",
                height: "18px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  backgroundColor: "#1976d2",
                  borderRadius: "3px",
                  minWidth: pct > 0 ? "4px" : "0",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#333",
                minWidth: "40px",
                textAlign: "right",
              }}
            >
              {formatNum(val)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatRevLabel(label, groupBy) {
  if (groupBy === "day") return DOW_NAMES[parseInt(label, 10)] || label;
  if (groupBy === "date") return (label || "").split("-")[2] || label; // "2026-02-07" → "07"
  return label;
}

function niceStep(max, numTicks) {
  const rawStep = max / numTicks || 1;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * mag;
}

function VerticalBarChart({ data, groupBy, loading }) {
  const SVG_W = 600;
  const SVG_H = 160;
  const PAD_L = 42,
    PAD_R = 4,
    PAD_T = 6,
    PAD_B = 20;
  const chartH = SVG_H - PAD_T - PAD_B;
  const chartW = SVG_W - PAD_L - PAD_R;

  if (loading) {
    return (
      <div
        style={{
          height: SVG_H,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={20} />
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: SVG_H,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#aaa",
          fontSize: 12,
        }}
      >
        No data for this period
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => d.revenue), 1);
  const numTicks = 4;
  const step = niceStep(maxVal, numTicks);
  const displayMax = step * Math.ceil(maxVal / step);
  const ticks = Array.from(
    { length: Math.round(displayMax / step) + 1 },
    (_, i) => i * step
  );

  const slotW = chartW / data.length;
  const barW = Math.max(5, Math.min(32, slotW * 0.55));

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      style={{ display: "block" }}
    >
      {ticks.map((tick, i) => {
        const y = PAD_T + chartH - (tick / displayMax) * chartH;
        return (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={SVG_W - PAD_R}
              y1={y}
              y2={y}
              stroke={tick === 0 ? "#d0d0d0" : "#f0f0f0"}
              strokeWidth={1}
            />
            <text
              x={PAD_L - 4}
              y={y + 3}
              textAnchor="end"
              fontSize={9}
              fill="#aaa"
            >
              {formatNum(tick)}
            </text>
          </g>
        );
      })}
      {data.map((item, i) => {
        const cx = PAD_L + slotW * i + slotW / 2;
        const barH =
          displayMax > 0
            ? Math.max(
                item.revenue > 0 ? 2 : 0,
                (item.revenue / displayMax) * chartH
              )
            : 0;
        const barY = PAD_T + chartH - barH;
        const label = formatRevLabel(item.label, groupBy);
        return (
          <g key={i}>
            <rect
              x={cx - barW / 2}
              y={barY}
              width={barW}
              height={barH}
              fill="#1976d2"
              rx={2}
              ry={2}
            />
            <text
              x={cx}
              y={SVG_H - 3}
              textAnchor="middle"
              fontSize={9}
              fill="#666"
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const useStyles = makeStyles(styles);

export default function Dashboard() {
  const classes = useStyles();

  // Shared period for all 4 stat cards (default: today)
  const [statPeriod, setStatPeriod] = React.useState("today");

  // Revenue summary card state
  const [revStats, setRevStats] = React.useState(null);

  // Net Revenue comparison card state
  const [revenueComp, setRevenueComp] = React.useState(null);

  // Revenue chart state
  const [revPeriod, setRevPeriod] = React.useState("month");
  const [revGroupBy, setRevGroupBy] = React.useState("date");
  const [revData, setRevData] = React.useState([]);
  const [revLoading, setRevLoading] = React.useState(false);

  // Products chart state
  const [productPeriod, setProductPeriod] = React.useState("month");
  const [productMetric, setProductMetric] = React.useState("revenue");
  const [productData, setProductData] = React.useState([]);
  const [productLoading, setProductLoading] = React.useState(false);

  // Return summary card state
  const [returnStats, setReturnStats] = React.useState(null);

  // Customers chart state
  const [customerPeriod, setCustomerPeriod] = React.useState("month");
  const [customerData, setCustomerData] = React.useState([]);
  const [customerLoading, setCustomerLoading] = React.useState(false);

  // Suppliers chart state
  const [supplierPeriod, setSupplierPeriod] = React.useState("month");
  const [supplierData, setSupplierData] = React.useState([]);
  const [supplierLoading, setSupplierLoading] = React.useState(false);

  // Load revenue summary card (re-fetches when statPeriod changes)
  React.useEffect(() => {
    setRevStats(null);
    gql(
      `query Q($p:String!){revenueSummary(period:$p){revenue invoiceCount}}`,
      { p: statPeriod }
    ).then(({ data }) => setRevStats(data?.revenueSummary || null));
  }, [statPeriod]);

  // Load net revenue comparison card
  React.useEffect(() => {
    gql(
      `query{revenueComparison{todayRevenue yesterdayRevenue changePercent}}`
    ).then(({ data }) => setRevenueComp(data?.revenueComparison || null));
  }, []);

  // Load return summary card (re-fetches when statPeriod changes)
  React.useEffect(() => {
    setReturnStats(null);
    gql(
      `query Q($p:String!){returnSummary(period:$p){totalRefund returnCount}}`,
      { p: statPeriod }
    ).then(({ data }) => setReturnStats(data?.returnSummary || null));
  }, [statPeriod]);

  // Load revenue chart
  React.useEffect(() => {
    setRevLoading(true);
    gql(
      `query Q($period:String!,$groupBy:String!){revenueChart(period:$period,groupBy:$groupBy){label revenue}}`,
      { period: revPeriod, groupBy: revGroupBy }
    )
      .then(({ data }) => setRevData(data?.revenueChart || []))
      .finally(() => setRevLoading(false));
  }, [revPeriod, revGroupBy]);

  // Load products chart
  React.useEffect(() => {
    setProductLoading(true);
    gql(
      `query Q($period:String!,$limit:Float!){topProducts(period:$period,limit:$limit){productId productName revenue quantity}}`,
      { period: productPeriod, limit: 10 }
    )
      .then(({ data }) => setProductData(data?.topProducts || []))
      .finally(() => setProductLoading(false));
  }, [productPeriod]);

  // Load customers chart
  React.useEffect(() => {
    setCustomerLoading(true);
    gql(
      `query Q($period:String!,$limit:Float!){topCustomers(period:$period,limit:$limit){customerId customerName totalSpend}}`,
      { period: customerPeriod, limit: 10 }
    )
      .then(({ data }) => setCustomerData(data?.topCustomers || []))
      .finally(() => setCustomerLoading(false));
  }, [customerPeriod]);

  // Load suppliers chart
  React.useEffect(() => {
    setSupplierLoading(true);
    gql(
      `query Q($period:String!,$limit:Float!){topSuppliers(period:$period,limit:$limit){supplierName totalRevenue}}`,
      { period: supplierPeriod, limit: 10 }
    )
      .then(({ data }) => setSupplierData(data?.topSuppliers || []))
      .finally(() => setSupplierLoading(false));
  }, [supplierPeriod]);

  return (
    <div>
      {/* ── Stat cards period selector ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "8px",
        }}
      >
        <DropdownButton
          options={REV_PERIODS}
          value={statPeriod}
          onChange={setStatPeriod}
        />
      </div>

      {/* ── Stat cards ── */}
      <GridContainer>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card style={{ maxHeight: "215px" }}>
            <CardHeader color="success" stats icon>
              <CardIcon color="success">
                <Store />
              </CardIcon>
              <p className={classes.cardCategory}>Revenue</p>
              <h3 className={classes.cardTitle}>
                {revStats ? formatNum(revStats.revenue) : "—"}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "12px",
                  color: "#999",
                  textAlign: "right",
                  minHeight: "16px",
                }}
              >
                {revStats
                  ? `${revStats.invoiceCount} invoice${
                      revStats.invoiceCount !== 1 ? "s" : ""
                    }`
                  : ""}
              </p>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                {REV_PERIODS.find((p) => p.value === statPeriod)?.label}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card style={{ maxHeight: "215px" }}>
            <CardHeader color="warning" stats icon>
              <CardIcon color="warning">
                <LocalOffer />
              </CardIcon>
              <p className={classes.cardCategory}>Invoices</p>
              <h3 className={classes.cardTitle}>
                {revStats ? revStats.invoiceCount : "—"}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "12px",
                  minHeight: "16px",
                }}
              />
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <Update />
                {REV_PERIODS.find((p) => p.value === statPeriod)?.label}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card style={{ maxHeight: "215px" }}>
            <CardHeader color="danger" stats icon>
              <CardIcon color="danger">
                <Store />
              </CardIcon>
              <p className={classes.cardCategory}>Net Revenue</p>
              <h3 className={classes.cardTitle}>
                {revenueComp ? (
                  <span
                    style={{
                      color:
                        revenueComp.changePercent >= 0 ? "#4caf50" : "#f44336",
                    }}
                  >
                    {revenueComp.changePercent >= 0 ? "+" : ""}
                    {revenueComp.changePercent.toFixed(2)}%
                  </span>
                ) : (
                  "—"
                )}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "12px",
                  minHeight: "16px",
                }}
              />
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                {revenueComp && revenueComp.yesterdayRevenue > 0 ? (
                  <>
                    {revenueComp.changePercent >= 0 ? (
                      <ArrowDropUp
                        style={{ color: "#4caf50", fontSize: "20px" }}
                      />
                    ) : (
                      <ArrowDropDown
                        style={{ color: "#f44336", fontSize: "20px" }}
                      />
                    )}
                    Compare with yesterday
                  </>
                ) : (
                  <>
                    <Update />
                    Compare with yesterday
                  </>
                )}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card style={{ maxHeight: "215px" }}>
            <CardHeader color="info" stats icon>
              <CardIcon color="info">
                <AssignmentReturn />
              </CardIcon>
              <p className={classes.cardCategory}>Return</p>
              <h3 className={classes.cardTitle}>
                {returnStats ? formatNum(returnStats.totalRefund) : "—"}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "12px",
                  color: "#999",
                  textAlign: "right",
                  minHeight: "16px",
                }}
              >
                {returnStats
                  ? `${returnStats.returnCount} return${
                      returnStats.returnCount !== 1 ? "s" : ""
                    }`
                  : ""}
              </p>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                {REV_PERIODS.find((p) => p.value === statPeriod)?.label}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ── Revenue chart + Suppliers chart ── */}
      <GridContainer>
        <GridItem xs={12} md={7}>
          <Card>
            <CardBody>
              {/* Header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  flexWrap: "wrap",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{ fontSize: "13px", fontWeight: 700, color: "#333" }}
                  >
                    Net Revenue
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1976d2",
                      backgroundColor: "#e3f2fd",
                      padding: "1px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    {formatNum(revData.reduce((s, d) => s + d.revenue, 0))}
                  </span>
                </div>
                <DropdownButton
                  options={REV_PERIODS}
                  value={revPeriod}
                  onChange={setRevPeriod}
                />
              </div>

              {/* Tabs */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #eee",
                  marginBottom: "8px",
                }}
              >
                {REV_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setRevGroupBy(tab.value)}
                    style={{
                      padding: "4px 10px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: revGroupBy === tab.value ? 600 : 400,
                      color: revGroupBy === tab.value ? "#1976d2" : "#888",
                      borderBottom:
                        revGroupBy === tab.value
                          ? "2px solid #1976d2"
                          : "2px solid transparent",
                      marginBottom: "-1px",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <VerticalBarChart
                data={revData}
                groupBy={revGroupBy}
                loading={revLoading}
              />
            </CardBody>
          </Card>
        </GridItem>

        {/* Top suppliers chart */}
        <GridItem xs={12} md={5}>
          <Card>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div
                  style={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
                >
                  Top 10 suppliers at best purchase receipt (excl. returns)
                </div>
                <DropdownButton
                  options={PERIODS}
                  value={supplierPeriod}
                  onChange={setSupplierPeriod}
                />
              </div>
              <HorizontalBarChart
                data={supplierData}
                valueKey="totalRevenue"
                labelKey="supplierName"
                loading={supplierLoading}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ── Charts ── */}
      <GridContainer>
        {/* Top products chart */}
        <GridItem xs={12} md={6}>
          <Card>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div
                  style={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
                >
                  Top 10 best-selling products
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  <DropdownButton
                    label=""
                    options={PRODUCT_METRICS}
                    value={productMetric}
                    onChange={setProductMetric}
                  />
                  <DropdownButton
                    label=""
                    options={PERIODS}
                    value={productPeriod}
                    onChange={setProductPeriod}
                  />
                </div>
              </div>
              <HorizontalBarChart
                data={productData}
                valueKey={productMetric === "revenue" ? "revenue" : "quantity"}
                labelKey="productName"
                loading={productLoading}
              />
            </CardBody>
          </Card>
        </GridItem>

        {/* Top customers chart */}
        <GridItem xs={12} md={6}>
          <Card>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div
                  style={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
                >
                  Top 10 customers by total spend
                </div>
                <DropdownButton
                  label=""
                  options={PERIODS}
                  value={customerPeriod}
                  onChange={setCustomerPeriod}
                />
              </div>
              <HorizontalBarChart
                data={customerData}
                valueKey="totalSpend"
                labelKey="customerName"
                loading={customerLoading}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
